export type ProcessStep = {
  body: readonly string[];
  name: string;
  why: string;
  whyLabel: string;
};

export type ProcessPhase = {
  heading: string;
  id: string;
  intro: string;
  label: string;
  steps: readonly ProcessStep[];
};

export const PROCESS_PHASES = [
  {
    heading: 'Start with numbers you can trust',
    id: 'with-aurora',
    intro:
      'The hardest part of going solar has never been the hardware. It is knowing whether you can be confident in the installer and the quote for your particular home and energy goals. That is the part we built the Marketplace to fix.',
    label: 'Steps 1–3 · With Aurora',
    steps: [
      {
        body: [
          'Enter your address, your monthly electric bill, and your utility. We model your actual roof from satellite imagery, its size, orientation, and shading, and combine that with your energy profile to size a system for your home.',
          'The prices come from participating local installers, applied to your modeled system, with cash, loan, and PPA or lease all priced up front. No account, no phone number, no waiting for a callback.',
        ],
        name: 'Get your estimate',
        why: 'The industry norm is a ballpark number designed to start a sales conversation. Our software is what 7,000+ solar companies use to design real systems, so your starting number is modeled to your roof, not a marketing average.',
        whyLabel: 'No guesswork pricing',
      },
      {
        body: [
          "Every participating installer serving your area appears in one view, with the same system priced the same way. Each card shows the installer's customer rating, Better Business Bureau accreditation, and years in business next to their numbers.",
          'Toggle between payment types, raise or lower the panel count, or add a battery, and watch every offer reprice live.',
        ],
        name: 'Compare installers side by side',
        why: 'Quotes from different companies are normally impossible to compare: different assumptions, different system sizes, different fine print. Like-for-like comparison removes the pressure tactics that thrive on confusion.',
        whyLabel: 'Apples to apples',
      },
      {
        body: [
          'When an offer looks right, you reach out, not the other way around. You share your name, phone, and email with that one installer, verified with a one-time passcode so the right person gets your details.',
          'Connecting with two or three installers is encouraged. Competing offers are your best negotiating tool, and your information never goes to anyone you did not pick.',
        ],
        name: 'Connect on your terms',
        why: 'Most solar lead sites sell your contact info to a list of companies the moment you type it in. That is why "getting a quote" usually means a week of unwanted calls. Here, your details go only to the installer you choose, when you choose.',
        whyLabel: 'Your info stays yours',
      },
    ],
  },
  {
    heading: 'Your installer takes it home',
    id: 'with-installer',
    intro:
      "From here, the licensed local professional you picked runs the project. They know your city's permits, your utility's process, and your roof. And most of the industry's top installers run this work on Aurora's design and delivery software, so the technology stays with you even after the introductions are made.",
    label: 'Steps 4–9 · With your installer',
    steps: [
      {
        body: [
          'Your installer visits the home and verifies what aerial imagery cannot: roof condition, electrical panel capacity, and shading at ground level. Then they confirm the final system design and price.',
          'The confirmed proposal may differ modestly from your Marketplace estimate. A big swing is uncommon, and worth questioning.',
        ],
        name: 'Site assessment & final design',
        why: 'Your installer puts your home through a more detailed design process with advanced modeling, which turns your estimate into precise numbers and an actual quote. Unless you and your installer make significant changes to the design, expect the final numbers to change only modestly, if they change at all.',
        whyLabel: 'From estimate to actual quote',
      },
      {
        body: [
          'Each installer you connected with turns their detailed design, sometimes shaped by that site visit, into a final proposal with a real quote. Review them side by side, ask your questions, and pick the installer and design you want to move forward with.',
          'Signing the contract is the moment the project becomes official, and it is what kicks off permitting and scheduling.',
        ],
        name: 'Choose your proposal & sign',
        why: 'This is why we recommend connecting with more than one installer on the Marketplace. Competing detailed proposals give you real leverage on price and terms, and a clear view of which team you want on your roof.',
        whyLabel: 'Competition works for you',
      },
      {
        body: [
          'Your installer files the local building and electrical permits and submits the interconnection application to your utility. If you have an HOA, this is when its paperwork gets handled too.',
          'This stage is mostly waiting on third parties, and it is the main reason the full journey commonly takes one to three months even though the install itself is quick.',
        ],
        name: 'Permits & paperwork',
        why: "The permit-ready plan sets that go to your city are often generated from your installer's Aurora design. Accurate, code-aware drawings are what get applications approved instead of bounced back, which keeps this waiting stage as short as it can be.",
        whyLabel: 'Plan sets that pass',
      },
      {
        body: [
          'The crew mounts the racking, sets the panels, installs the inverter, and wires the system. For most homes this takes one or two days, and you usually do not need to do anything except be reachable.',
        ],
        name: 'Installation day',
        why: 'The layout the crew builds from is the Aurora design your installer finalized: panel placement, stringing, and electrical details worked out before anyone climbs a ladder. Precise designs mean fewer surprises and change orders once the crew is on the roof.',
        whyLabel: 'Built from the blueprint',
      },
      {
        body: [
          'A local inspector signs off on the work, then your utility grants permission to operate (PTO), its formal approval to connect your system to the grid.',
          "If the process ever feels stalled, it is almost always here, in the utility's queue rather than anything on your roof.",
        ],
        name: 'Inspection & permission to operate',
        why: 'Inspectors and utilities check the install against the submitted plans. When the design and plan set come from Aurora, what is on the roof matches what is on paper, which is exactly what a clean sign-off needs. Knowing the PTO queue exists helps too, which is why it is on this page.',
        whyLabel: 'A cleaner sign-off',
      },
      {
        body: [
          'Your installer flips the system on, walks you through the monitoring app, and hands over your warranty documents. From this day forward your panels are producing, and your equipment, performance, and workmanship warranties are in effect.',
        ],
        name: 'Switch on',
        why: "The production forecast behind your savings numbers came from Aurora's simulation engine, the one independently validated by NREL, so day-one output should look like the plan. From here, your installer and your monitoring app are the go-to for any performance questions.",
        whyLabel: 'Performance as promised',
      },
    ],
  },
] as const satisfies readonly ProcessPhase[];

export const HOWTO_STEPS = PROCESS_PHASES.flatMap((phase) =>
  phase.steps.map(({ body, name }) => ({ name, text: body.join(' ') }))
);
